import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, catchError, throwError } from 'rxjs';
import { PublicApi } from '../constants';
import { AppResponse } from '../constants/ApiResponse';
import { environment } from '../../environments/environment';

// Define a type for the keys of PublicApi
type PublicApiKeys = keyof typeof PublicApi | 'BILLING_DISCOUNT';

@Injectable({
  providedIn: 'root',
})
export class PagesService {
  constructor(private http: HttpClient) { }

  getContestList(endpoint: PublicApiKeys, page: number, perPage: number, startDate?: any, endDate?: any): Observable<any> {
    let params = new HttpParams()
      .set('paging[page]', page.toString())
      .set('paging[per_page]', perPage.toString());

    if (startDate) {
      params = params.set('filters[start_date]', startDate);
    }
    if (endDate) {
      params = params.set('filters[end_date]', endDate);
    }

    const apiUrl = PublicApi[endpoint];
    return this.http.get<any>(apiUrl, { params });
  }

  getParticipant(endpoint: PublicApiKeys, id?: any): Observable<any> {
    let params = new HttpParams();

    if (id !== undefined && id !== null) {
      params = params.set('contest_id', id);
    }

    const apiUrl = PublicApi[endpoint];
    return this.http.get<any>(apiUrl);
  }

  getCards(endpoint: PublicApiKeys, id?: any): Observable<any> {
    let params = new HttpParams();

    if (id !== undefined && id !== null) {
      params = params.set('user_id', id);
    }

    const apiUrl = PublicApi[endpoint];
    return this.http.get<any>(apiUrl, { params });
  }

  getCardDetail(endpoint: PublicApiKeys, id?: any): Observable<any> {
    const apiBaseUrl = PublicApi[endpoint];
    const apiUrl = id !== undefined && id !== null ? `${apiBaseUrl}/${id}` : apiBaseUrl;
    return this.http.get<any>(apiUrl);
  }

  updateCardDetail(endpoint: PublicApiKeys, id: any, data: any): Observable<any> {
    const apiBaseUrl = PublicApi[endpoint];
    const apiUrl = id !== undefined && id !== null ? `${apiBaseUrl}/${id}` : apiBaseUrl;
    return this.http.patch<any>(apiUrl, data);
  }

  deleteCard(endpoint: PublicApiKeys, id: any): Observable<any> {
    const apiBaseUrl = PublicApi[endpoint];
    const apiUrl = id !== undefined && id !== null ? `${apiBaseUrl}/${id}` : apiBaseUrl;
    return this.http.delete<any>(apiUrl);
  }

  deleteWithBody(endpoint: PublicApiKeys, body: any): Observable<any> {
    const apiUrl = PublicApi[endpoint];
    return this.http.request<any>('DELETE', apiUrl, { body });
  }

  getAll(endpoint: PublicApiKeys, queryParams?: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];

    // Convert query params to HttpParams if provided
    let params = new HttpParams();
    if (queryParams) {
      Object.keys(queryParams).forEach(key => {
        // Skip undefined, null, and empty string values
        const value = queryParams[key];
        if (value === undefined || value === null || value === '') {
          return;
        }

        if (Array.isArray(value)) {
          if (value.length === 0) {
            return;
          }

          value.forEach((item) => {
            if (item !== undefined && item !== null && item !== '') {
              params = params.append(`${key}[]`, String(item));
            }
          });
          return;
        }

        params = params.set(key, value.toString());
      });
    }

    return this.http.get<AppResponse>(apiUrl, queryParams ? { params } : {});
  }

  delete(endpoint: PublicApiKeys): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.delete<AppResponse>(apiUrl);
  }

  update(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, model);
  }

  put(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.put<AppResponse>(apiUrl, model);
  }

  patch(endpoint: PublicApiKeys, id: string | number, model: any): Observable<AppResponse> {
    const apiUrl = `${PublicApi[endpoint]}/${id}`;
    return this.http.patch<AppResponse>(apiUrl, model);
  }

  updateWithId(endpoint: PublicApiKeys, model: any, id: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(`${apiUrl}?email=${id}`, model);
  }

  getById(endpoint: PublicApiKeys, id: any): Observable<AppResponse> {
    const apiUrl = `${PublicApi[endpoint]}/${id}`;
    return this.http.get<AppResponse>(apiUrl);
  }

  getByCustomName(endpoint: PublicApiKeys, customName: string, id: any): Observable<AppResponse> {
    const apiUrl = `${PublicApi[endpoint]}?${customName}=${id}`;
    return this.http.get<AppResponse>(apiUrl);
  }

  filterNonEmptyValues(obj: any): Partial<any> {
    const filteredObj: Partial<any> = {};

    for (const key in obj) {
      if (obj[key as keyof any] !== '') {
        filteredObj[key as any] = obj[key as keyof any];
      }
    }

    return filteredObj;
  }

  convertObjectToParams(obj: any): HttpParams {
    let params = new HttpParams();
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        if (obj[key]) params = params.set(key, obj[key]);
      }
    }
    return params;
  }

  VerifyEmailOTPSv(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, model);
  }

  VerifyPhoneNumberOTPSv(endpoint: PublicApiKeys, code: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(`${apiUrl}?code=${code}`, null);
  }

  PageClaim(claimPageUrl: string, claimPageData: any) {
    return this.http.post(claimPageUrl, claimPageData);
  }

  create(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, model);
  }

  verify(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, model);
  }

  getByIdByQuerry(endpoint: PublicApiKeys, id: any): Observable<AppResponse> {
    const apiUrl = `${PublicApi[endpoint]}?id=${id}`;
    return this.http.get<AppResponse>(apiUrl);
  }

  resetPassword(endpoint: PublicApiKeys, model: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, model);
  }
  getAllById(endpoint: PublicApiKeys, params?: any): Observable<AppResponse> {
    // ✅ Build URL dynamically with query params
    const apiUrl = `${PublicApi[endpoint]}${params ? '?' + new URLSearchParams(params).toString() : ''}`;
    return this.http.get<AppResponse>(apiUrl);
  }

  // POST request with JSON body for filtered data
  postWithFilters(endpoint: PublicApiKeys, body: any): Observable<AppResponse> {
    const apiUrl = PublicApi[endpoint];
    return this.http.post<AppResponse>(apiUrl, body);
  }

  getContestPerformance(contestId: number | string): Observable<AppResponse> {
    const apiUrl = PublicApi.CONTEST_PERFORMANCE;
    const params = new HttpParams().set('contest_id', String(contestId));

    return this.http.get<AppResponse>(apiUrl, { params }).pipe(
      catchError((error) => {
        if (error?.status === 404) {
          const fallbackUrl = apiUrl.replace('Contest_Performance', 'contest_performance');
          if (fallbackUrl !== apiUrl) {
            return this.http.get<AppResponse>(fallbackUrl, { params });
          }
        }
        return throwError(() => error);
      })
    );
  }
}
